import { useEffect } from 'react';
import type { ChatInstance } from '@leagueplatform/chat';
import {
  consumePendingInitialMessage,
  markInitialMessageDispatched,
  subscribePendingInitialMessage,
} from 'common/pending-initial-message';

// Auto-sends a message stashed by the live-chat entry page once the given
// chat instance's session reaches `active.ready` — the same readiness gate
// the SDK's own Composer uses before allowing a send. Must be subscribed on
// the SAME instance the entry page starts the session on (the shared
// `chatInstance` singleton, not a demo run's throwaway instance) or the
// stashed message is never observed.
export const useAutoSendInitialMessage = (chatInstance: ChatInstance) => {
  useEffect(() => {
    const trySend = () => {
      const snapshot = chatInstance.actor.getSnapshot();
      const isReady = !!snapshot.context.currentSession?.matches({
        active: 'ready',
      });
      if (!isReady) return;

      const pending = consumePendingInitialMessage();
      if (!pending) return;
      if (markInitialMessageDispatched(pending.dispatchId)) return;

      chatInstance.actor.send({
        type: 'OUTGOING_MESSAGE',
        body: pending.message,
      });
    };

    trySend();
    const subscription = chatInstance.actor.subscribe(trySend);
    // Also react to a message being stashed directly, rather than relying
    // solely on the chat actor to happen to re-emit afterwards (e.g. today
    // it does, via the SDK's own SET_VISIBILITY dispatch — but that's an
    // implementation detail of a dependency, not a contract this hook should
    // depend on). Covers a session that's already ready when the message is
    // stashed, with no actor state change left to observe.
    const unsubscribePending = subscribePendingInitialMessage(trySend);
    return () => {
      subscription.unsubscribe();
      unsubscribePending();
    };
  }, [chatInstance]);
};
